<template>
  <div ref="container" :class="{ 'oc-fullscreen': is_fullscreen }">
    <div class="d-flex align-center flex-wrap mb-4">
      <span class="text-uppercase text-h6 font-weight-regular">{{ title }}</span>
      <v-spacer></v-spacer>
      <v-text-field v-model="search" append-icon="search" placeholder="Buscar puesto o persona" single-line hide-details dense class="mr-6" style="max-width: 420px"></v-text-field>
      <v-btn outlined small icon tile color="blue-grey" title="Alejar" @click="setZoom(zoom - ZOOM_STEP)" :disabled="zoom <= ZOOM_MIN"><v-icon small>remove</v-icon></v-btn>
      <span class="oc-toolbar-zoom">{{ Math.round(zoom * 100) }}%</span>
      <v-btn outlined small icon tile color="blue-grey" class="mr-2" title="Acercar" @click="setZoom(zoom + ZOOM_STEP)" :disabled="zoom >= ZOOM_MAX"><v-icon small>add</v-icon></v-btn>
      <v-btn outlined small icon tile color="blue-grey" :title="is_fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'" @click="toggleFullscreen">
        <v-icon small>{{ is_fullscreen ? "fullscreen_exit" : "fullscreen" }}</v-icon>
      </v-btn>
    </div>

    <div class="oc-canvas" @dragover.prevent="() => {}">
      <div v-if="loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <v-alert v-else-if="tree.length == 0" type="info" outlined class="ma-4">No hay puestos para mostrar. Cree un puesto o importe la estructura.</v-alert>
      <div v-else class="oc-chart" :style="{ zoom: zoom }">
        <OrgChartNode v-for="node in tree" :key="node.id" :node="node" :depth="0" />
      </div>
    </div>

    <div class="d-flex align-center flex-wrap mt-4">
      <div class="oc-legend">
        <span v-for="legend in legend_items" :key="legend.style" class="oc-legend-item"><span class="oc-swatch" :class="legend.style"></span>{{ legend.name }}</span>
        <span class="oc-legend-item"><span class="oc-swatch vacant"></span>Vacante</span>
      </div>
      <v-spacer></v-spacer>
      <span v-if="update_access" class="text-caption grey--text text--darken-1">Arrastre un puesto sobre otro para cambiar su dependencia</span>
    </div>
  </div>
</template>

<script>
import OrgChartNode from "./OrgChartNode.vue";
import { NODE_STYLES } from "@/constants/org_chart";

export default {
  components: { OrgChartNode },
  provide() {
    return { diagram: this };
  },
  props: {
    title: { type: String, default: "Organigrama" },
    tree: Array,
    node_types: { type: Array, default: () => [] },
    loading: Boolean,
    create_access: Boolean,
    update_access: Boolean,
    delete_access: Boolean,
  },
  data() {
    return {
      ZOOM_MIN: 0.4,
      ZOOM_MAX: 1.5,
      ZOOM_STEP: 0.1,
      zoom: 1,
      is_fullscreen: false,
      search: "",
      // id => expandido, sobrescribe el valor inicial children_collapsed del puesto
      expanded: {},
      dragging_node: null,
    };
  },
  computed: {
    // Tipos agrupados por estilo para la leyenda (ej. "Jefatura / Área")
    legend_items() {
      const result = [];
      NODE_STYLES.forEach((style) => {
        const names = this.node_types.filter((node_type) => node_type.style == style.id).map((node_type) => node_type.name);
        if (names.length > 0) result.push({ style: style.id, name: names.join(" / ") });
      });
      return result;
    },
    // id => { node, parent_id } de todo el arbol
    nodes_by_id() {
      const result = {};
      const visit = (nodes, parent_id) =>
        nodes.forEach((node) => {
          result[node.id] = { node, parent_id };
          visit(node.children || [], node.id);
        });
      visit(this.tree || [], null);
      return result;
    },
    search_match_ids() {
      const text = this.normalize(this.search);
      if (text.length < 2) return null;

      const ids = {};
      Object.values(this.nodes_by_id).forEach(({ node }) => {
        const legajo = node.legajo ? node.legajo.name + " " + node.legajo.surname + " " + node.legajo.surname + " " + node.legajo.name : "";
        if (this.normalize(node.name + " " + legajo).includes(text)) ids[node.id] = true;
      });
      return ids;
    },
  },
  watch: {
    // Se expanden los superiores de los puestos encontrados
    search_match_ids(ids) {
      if (ids == null) return;
      Object.keys(ids).forEach((id) => {
        let parent_id = this.nodes_by_id[id].parent_id;
        while (parent_id != null) {
          this.$set(this.expanded, parent_id, true);
          parent_id = this.nodes_by_id[parent_id].parent_id;
        }
      });
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },
  methods: {
    normalize(text) {
      return (text || "")
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
    },
    setZoom(value) {
      this.zoom = Math.min(this.ZOOM_MAX, Math.max(this.ZOOM_MIN, Math.round(value * 10) / 10));
    },
    toggleFullscreen() {
      if (document.fullscreenElement) document.exitFullscreen();
      else this.$refs.container.requestFullscreen();
    },
    onFullscreenChange() {
      this.is_fullscreen = document.fullscreenElement === this.$refs.container;
    },
    isExpanded(node) {
      return node.id in this.expanded ? this.expanded[node.id] : !node.children_collapsed;
    },
    toggle(node) {
      this.$set(this.expanded, node.id, !this.isExpanded(node));
    },
    isSearchMatch(node) {
      return this.search_match_ids != null && node.id in this.search_match_ids;
    },
    isDimmedBySearch(node) {
      return this.search_match_ids != null && !(node.id in this.search_match_ids);
    },
    selectNode(node) {
      this.$emit("editNode", node);
    },
    onDragStart(e, node) {
      if (!this.update_access) return;
      this.dragging_node = node;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", node.id);
    },
    onDragEnd() {
      this.dragging_node = null;
    },
    // No se puede soltar sobre si mismo, su padre actual ni sus dependientes
    canDropOn(target) {
      if (this.dragging_node == null || target.id == this.dragging_node.id || target.id == this.dragging_node.parent_id) return false;

      let parent_id = this.nodes_by_id[target.id].parent_id;
      while (parent_id != null) {
        if (parent_id == this.dragging_node.id) return false;
        parent_id = this.nodes_by_id[parent_id].parent_id;
      }
      return true;
    },
    onDrop(target) {
      if (!this.canDropOn(target)) return;
      this.$emit("moveNode", { node: this.dragging_node, parent: target });
      this.dragging_node = null;
    },
  },
};
</script>
